import React, { createContext, useContext, useRef } from "react";
import { produce } from "immer";
import { mergeAll, zipObj } from "ramda";
import { createStore, useStore } from "zustand";
import { devtools } from "zustand/middleware";

import {
    fundsSchema,
    shareClassSchema,
} from "@hsl/core/fund-explorer/metaSchemas";
import { createFuzzySearch } from "@hsl/core/fund-explorer/search";
import {
    downloadCSV,
    fetchFundExplorerData,
    indexData,
    type CSVData,
    type CSVFieldConfig,
} from "@hsl/core/fund-explorer/services";
import {
    type FundData,
    type ListingData,
    type ShareClassData,
    type TopLevelDataType,
} from "@hsl/core/fund-explorer/types";
import dayjs from "@hsl/lgim-explorer/src/lib/dayjs";

import { config as defaultConfig } from "../config/default.config";
import { processConfig, processFilters } from "../config/helpers";
import type { Config, SearchConfig } from "../config/types";
import { useAllTranslations } from "../hooks/useTranslation";
import {
    appliedFiltersToSearchParams,
    applyGroupBy,
    downloadFilename,
    filterHelper,
    generateLocalId,
    searchHelper,
    searchParamsToAppliedFilters,
    sortHelper,
} from "./helpers";
import type {
    Filter,
    FundExplorerState,
    GroupedData,
    ShareClassWithMeta,
} from "./types";

export const createExplorerStore = () =>
    createStore<FundExplorerState>()(
        devtools(
            (set, get) =>
                ({
                    config: defaultConfig,
                    rawData: undefined,
                    indexedData: null,
                    objectIds: { funds: [], shareClasses: [], listings: [] },
                    refinedData: {
                        funds: [],
                        shareClasses: [],
                        listings: [],
                    },
                    groupedData: {},
                    isGrouped: false,
                    groupBy: "",
                    loading: true,
                    errors: [],
                    messages: [],
                    filters: {
                        primary: [],
                        secondary: [],
                        advanced: [],
                    },
                    isFiltering: false,
                    search: {
                        searchFn: () => [],
                        term: "",
                        results: null,
                        isActive: false,
                        rawResults: [],
                    },
                    pageSections: [],
                    activeShareClass: null,
                    cardMode: "card",
                    translations: {},
                    sortBy: null,
                    sortDirection: "desc",
                    setSort(sortBy) {
                        const { sortBy: currentSortBy, sortDirection } = get();

                        set(
                            produce((state: FundExplorerState) => {
                                if (sortBy === currentSortBy) {
                                    state.sortDirection =
                                        sortDirection === "asc"
                                            ? "desc"
                                            : "asc";
                                } else {
                                    state.sortBy = sortBy;
                                    state.sortDirection = "desc";
                                }
                            }),
                            false,
                            { type: `set sort to ${sortBy}` },
                        );
                        get().refine({});
                    },
                    setCardMode(mode) {
                        set(
                            produce((state: FundExplorerState) => {
                                state.cardMode = mode;
                            }),
                            false,
                            { type: "set card mode" },
                        );
                    },
                    async fetchRawData(processedConfig: Config) {
                        const { url, id, audienceId, languageId } =
                            processedConfig;
                        const rawData = await fetchFundExplorerData(
                            `${url}/${id}?audience=${audienceId}&language=${languageId}`,
                        );
                        set(
                            produce((state: FundExplorerState) => {
                                state.rawData = rawData;
                            }),
                            false,
                            { type: "get raw data" },
                        );
                    },
                    mergeConfigs(processedConfig: Config) {
                        set(
                            produce((state: FundExplorerState) => {
                                state.config = mergeAll([
                                    defaultConfig,
                                    processedConfig,
                                ]);
                            }),
                            false,
                            { type: "merge configs (processed/default)" },
                        );
                    },
                    indexData() {
                        set(
                            produce((state: FundExplorerState) => {
                                const { rawData, indexedData } = get();
                                state.indexedData = rawData
                                    ? indexData(rawData)
                                    : indexedData;
                            }),
                            false,
                            { type: "index data" },
                        );
                    },
                    processConfigFilters() {
                        const { indexedData, config } = get();
                        const {
                            audienceId,
                            usertypeId,
                            languageId,
                            jurisdictionId,
                            locale,
                        } = config;
                        set(
                            produce((state: FundExplorerState) => {
                                state.config.filters = processFilters(
                                    config.filters,
                                    indexedData!,
                                    {
                                        audienceId,
                                        usertypeId,
                                        languageId,
                                        jurisdictionId,
                                        locale,
                                    },
                                );
                            }),
                            false,
                            { type: "process filters" },
                        );
                    },
                    createSearch(config: SearchConfig) {
                        set(
                            produce((state: FundExplorerState) => {
                                state.search.searchFn = createFuzzySearch(
                                    config ? config.searchOn : [],
                                    get().indexedData!,
                                    config.minChars,
                                );
                            }),
                            false,
                            { type: "create search" },
                        );
                    },
                    async init(config, prefetchedData) {
                        try {
                            const processedConfig = processConfig(config);
                            const { search: searchConfig } = processedConfig;

                            dayjs.locale(processedConfig.locale.slice(0, 2));

                            get().mergeConfigs(processedConfig);

                            if (prefetchedData) {
                                set(
                                    produce((state: FundExplorerState) => {
                                        state.rawData = {
                                            metadata: prefetchedData.metadata,
                                            funds: [],
                                        };
                                        state.indexedData =
                                            prefetchedData.indexedData;
                                    }),
                                    false,
                                    { type: "set provided index data" },
                                );
                            } else {
                                await get().fetchRawData(processedConfig);

                                get().indexData();
                            }

                            get().processConfigFilters();

                            get().createSearch(searchConfig);

                            set(
                                produce((state: FundExplorerState) => {
                                    if (state.indexedData) {
                                        const getTypeIds = (
                                            type: TopLevelDataType,
                                        ) =>
                                            Object.entries(state.indexedData!)
                                                .filter(
                                                    ([_, obj]) =>
                                                        obj.type === type,
                                                )
                                                .map(([id]) => id);
                                        state.objectIds.funds =
                                            getTypeIds("fund");
                                        state.objectIds.shareClasses =
                                            getTypeIds("shareclass");
                                        state.objectIds.listings =
                                            getTypeIds("listing");
                                    }
                                }),
                                false,
                                { type: "init: set data" },
                            );
                            get().applySearchParams();
                        } catch (err) {
                            console.log(err);
                            set(() => ({
                                errors: [{ detail: JSON.stringify(err) }],
                            }));
                        } finally {
                            set(() => ({ loading: false }), false, {
                                type: "init: loading complete",
                            });
                        }
                    },
                    getFieldMetaForCode(type, codeName) {
                        const { rawData } = get();
                        const meta = rawData?.metadata
                            ? rawData.metadata[type]
                            : [];
                        return meta.find((obj) => obj.code_name === codeName);
                    },
                    getMeta(key) {
                        return get().rawData?.metadata[key];
                    },
                    getDataById(id) {
                        const { indexedData } = get();
                        return indexedData ? indexedData[id] : undefined;
                    },
                    getFundById(id) {
                        const { indexedData } = get();
                        if (!indexedData) {
                            return undefined;
                        }
                        const dataWithType = {
                            type: get().config.productType,
                            data: indexedData[id],
                        };
                        return fundsSchema.parse(dataWithType);
                    },
                    getShareClassById(id) {
                        const { indexedData } = get();
                        if (!indexedData) {
                            return undefined;
                        }
                        const dataWithType = {
                            type: get().config.productType,
                            data: indexedData[id],
                        };
                        return shareClassSchema.parse(dataWithType);
                    },
                    refine(args, reset = false, history = true) {
                        const state = get();
                        const {
                            term,
                            isActiveSearch,
                            searchResults,
                            rawResults,
                        } = searchHelper(state, args.term?.trim(), reset);
                        const {
                            primary,
                            secondary,
                            advanced,
                            hasFundFilters,
                            hasShareClassFilters,
                            hasListingFilters,
                            filteredData,
                        } = filterHelper(state, args, searchResults, reset);
                        const refinedData = {
                            funds: sortHelper(
                                state,
                                hasFundFilters
                                    ? filteredData.funds
                                    : searchResults.funds,
                            ),
                            shareClasses: hasShareClassFilters
                                ? filteredData.shareClasses
                                : searchResults.shareClasses,
                            listings: hasListingFilters
                                ? filteredData.listings
                                : searchResults.listings,
                        };

                        const groupBy = reset
                            ? ""
                            : args.groupBy ?? state.groupBy;
                        const groupedData = applyGroupBy(
                            state.getDataById,
                            refinedData.funds,
                            groupBy,
                        );

                        set(
                            produce((state: FundExplorerState) => {
                                state.search.term = term;
                                state.search.isActive = isActiveSearch;
                                state.search.results = isActiveSearch
                                    ? searchResults
                                    : null;
                                state.search.rawResults = rawResults;
                                state.filters.primary = primary;
                                state.filters.secondary = secondary;
                                state.filters.advanced = advanced;
                                state.isFiltering =
                                    hasFundFilters ||
                                    hasShareClassFilters ||
                                    hasListingFilters;
                                state.refinedData = refinedData;
                                state.groupBy = groupBy;
                                state.isGrouped = groupBy.length > 0;
                                state.groupedData = groupedData as GroupedData;
                            }),
                            false,
                            { type: "refine" },
                        );

                        if (history) {
                            appliedFiltersToSearchParams({
                                ...get().filters,
                                term: get().search.term,
                            });
                        }
                    },
                    applySearchParams() {
                        const state = get();
                        const srchFilters = searchParamsToAppliedFilters(
                            state.config.filters,
                        );
                        state.refine(
                            srchFilters,
                            Object.keys(srchFilters).length === 0,
                            false,
                        );
                    },
                    applyFilter(filter: Filter) {
                        const keep = Boolean(
                            Array.isArray(filter.value)
                                ? filter.value.length
                                : filter.value,
                        );
                        const state = get();
                        const filterPropsWithAssociatedFilters = [
                            filter.prop,
                            ...Object.keys(filter?.associatedFilters ?? {}),
                        ];
                        const allOtherFilters = state.filters[
                            filter.type
                        ].filter(
                            (f) =>
                                !filterPropsWithAssociatedFilters.includes(
                                    f.prop,
                                ),
                        );

                        const newFilters = [filter];
                        if (filter.associatedFilters) {
                            const associatedFilters = filter.associatedFilters;
                            Object.keys(filter.associatedFilters).forEach(
                                (filterKey) => {
                                    newFilters.push({
                                        prop: filterKey,
                                        level: filter.level,
                                        type: filter.type,
                                        value:
                                            associatedFilters &&
                                            (associatedFilters[
                                                filterKey as keyof typeof filter.associatedFilters
                                            ] ??
                                                []),
                                    });
                                },
                            );
                        }

                        state.refine({
                            [filter.type]: keep
                                ? allOtherFilters.concat(newFilters)
                                : allOtherFilters,
                        });
                    },
                    // Rename this --> filterHasNoFunds
                    filterHasFunds(filter: Filter) {
                        const state = get();
                        const filterPropsWithAssociatedFilters = [
                            filter.prop,
                            ...Object.keys(filter?.associatedFilters ?? {}),
                        ];
                        const allOtherFilters = state.filters[
                            filter.type
                        ].filter(
                            (f) =>
                                !filterPropsWithAssociatedFilters.includes(
                                    f.prop,
                                ),
                        );
                        const args = {
                            ...state.filters,
                            [filter.type]: allOtherFilters.concat([filter]),
                        };
                        const searchResults = state.search.isActive
                            ? state.search.results ?? {
                                  funds: [],
                                  shareClasses: [],
                                  listings: [],
                              }
                            : state.objectIds;
                        const availableData =
                            allOtherFilters.length > 0
                                ? filterHelper(
                                      state,
                                      {
                                          ...state.filters,
                                          [filter.type]: allOtherFilters,
                                      },
                                      searchResults,
                                      false,
                                  ).filteredData
                                : searchResults;
                        const {
                            filteredData: { funds, listings, shareClasses },
                        } = filterHelper(state, args, availableData, false);
                        const {
                            config: { productType },
                            refinedData,
                        } = state;
                        const nothingDisplaying =
                            refinedData.funds.length === 0 ||
                            refinedData.shareClasses.length === 0 ||
                            (productType === "etf" &&
                                refinedData.listings.length === 0);

                        const lstCondition =
                            productType === "etf" && listings.length === 0;
                        const scCondition =
                            shareClasses.length === 0 || lstCondition;
                        const fundCondition = funds.length === 0 || scCondition;
                        return (
                            nothingDisplaying ||
                            (filter.level === "fund" && fundCondition) ||
                            (filter.level === "shareclass" && scCondition) ||
                            (filter.level === "listing" && lstCondition)
                        );
                    },
                    clearFilters() {
                        set(
                            produce((state: FundExplorerState) => {
                                state.filters = {
                                    primary: [],
                                    secondary: [],
                                    advanced: [],
                                };
                            }),
                            false,
                            { type: "clear filters" },
                        );
                        get().refine({});
                    },
                    applySearch(term: string) {
                        if (term === get().search.term) return;
                        get().refine({ term });
                    },
                    applyGroupBy(prop: string) {
                        get().refine({ groupBy: prop });
                    },
                    setActiveShareClass(id: string) {
                        const active = get().getDataById(id);
                        if (!active) {
                            console.error(`No share class found with id ${id}`);
                            return;
                        }
                        set(
                            produce((state: FundExplorerState) => {
                                state.activeShareClass =
                                    active as ShareClassWithMeta;
                            }),
                            false,
                            { type: "set active share class" },
                        );
                    },
                    downloadCSVData(refinedOnly = false) {
                        const state = get();
                        const isFund = (
                            o: FundData | undefined,
                        ): o is FundData => Boolean(o);
                        const isShareClass = (
                            o: ShareClassData | undefined,
                        ): o is ShareClassData => Boolean(o);
                        const isListing = (
                            o: ListingData | undefined,
                        ): o is ListingData => Boolean(o);
                        type RawData =
                            | string
                            | boolean
                            | null
                            | [string, string, string, string];
                        const zipData =
                            (meta: { code_name: string }[]) =>
                            (data: RawData[]) =>
                                zipObj(
                                    meta.map((m) => m.code_name),
                                    data,
                                );
                        const zipFundData = zipData(
                            state.rawData!.metadata.fund_fields,
                        );
                        const zipSCData = zipData(
                            state.rawData!.metadata.share_class_fields,
                        );
                        const zipListingData = state.rawData?.metadata
                            .listing_fields
                            ? zipData(state.rawData.metadata.listing_fields)
                            : null;
                        const gFID = generateLocalId("fund");
                        const applyFieldOverrides = (field: CSVFieldConfig) =>
                            field.overrides?.reduce((oField, o) => {
                                if (
                                    o.type === "jurisdiction" &&
                                    o.value.includes(
                                        state.config.jurisdictionId,
                                    )
                                ) {
                                    return { ...oField, ...o.override };
                                }
                                return oField;
                            }, field) ?? field;

                        let data: CSVData[] = [];
                        (refinedOnly
                            ? state.refinedData.funds
                            : state.objectIds.funds
                        )
                            .map((id) => {
                                const fund = state.rawData?.funds.find(
                                    (f) => gFID(f.id) === id,
                                );
                                return fund && fund.data
                                    ? ({
                                          ...fund,
                                          data: zipFundData(
                                              fund.data as RawData[],
                                          ),
                                      } as FundData)
                                    : undefined;
                            })
                            .filter(isFund)
                            .map((fund) => {
                                (fund.share_classes as ShareClassData[])
                                    .map(
                                        (sc) =>
                                            ({
                                                ...sc,
                                                data: zipSCData(
                                                    sc.data as unknown as RawData[],
                                                ),
                                            }) as ShareClassData,
                                    )
                                    .filter(isShareClass)
                                    .forEach((shareclass) => {
                                        if (shareclass.listings) {
                                            (
                                                shareclass.listings as ListingData[]
                                            )
                                                ?.map((ls) =>
                                                    zipListingData
                                                        ? ({
                                                              ...ls,
                                                              data: zipListingData(
                                                                  ls.data as unknown as RawData[],
                                                              ),
                                                          } as ListingData)
                                                        : undefined,
                                                )
                                                .filter(isListing)
                                                .forEach((listing) => {
                                                    data = [
                                                        ...data,
                                                        {
                                                            fund,
                                                            shareclass,
                                                            listing,
                                                        },
                                                    ];
                                                });
                                        } else {
                                            data = [
                                                ...data,
                                                {
                                                    fund,
                                                    shareclass,
                                                    listing: {} as ListingData,
                                                },
                                            ];
                                        }
                                    });
                            });
                        downloadCSV(
                            state.config.downloadFields.map((field) => {
                                const oField = applyFieldOverrides(field);
                                const txLabel =
                                    state.translations[oField.label];
                                const label =
                                    txLabel && txLabel.length > 0
                                        ? txLabel
                                        : oField.label;
                                return {
                                    ...oField,
                                    label,
                                };
                            }),
                            downloadFilename(
                                state.config.downloadFileName ??
                                    "fund-explorer.csv",
                                state.translations,
                            ),
                            data,
                        );
                    },
                    setTranslations(translations: Record<string, string>) {
                        set(
                            produce((state: FundExplorerState) => {
                                state.translations = translations;
                            }),
                            false,
                            { type: "translations set" },
                        );
                    },
                }) as FundExplorerState,
            {
                name: "FundExplorerStore",
                enabled: process.env.NODE_ENV !== "production",
            },
        ),
    );

type StoreType = ReturnType<typeof createExplorerStore>;

export const ExplorerStoreContext = createContext<StoreType | null>(null);

export const ExplorerStoreProvider = ({
    children,
    config,
}: {
    children: React.ReactNode;
    config: Config;
}) => {
    const storeRef = useRef<StoreType | null>(null);
    const translations = useAllTranslations();

    if (!storeRef.current) {
        storeRef.current = createExplorerStore();
        void storeRef.current.getState().init(config);
        // while most translations use a separate context, this is used
        // for translating the CSV download
        storeRef.current.getState().setTranslations(translations);
    }

    return (
        <ExplorerStoreContext.Provider value={storeRef.current}>
            {children}
        </ExplorerStoreContext.Provider>
    );
};

type ExtractState<S> = S extends {
    getState: () => infer T;
}
    ? T
    : never;

const useExplorerStore = <T,>(
    selector: (state: ExtractState<StoreType>) => T,
    equalityFn?: (a: T, b: T) => boolean,
) => {
    const store = useContext(ExplorerStoreContext);
    if (!store) {
        throw new Error(
            "useExplorerStore must be used within a ExplorerStoreProvider",
        );
    }
    return useStore(store, selector, equalityFn);
};

export default useExplorerStore;
